// ============================================================
// dev-core-web/src/hooks/useAuth.ts
// Version: 1.1.2
// Purpose:
//   - тонкий React-адаптер над store/authStore.ts
//   - предоставляет user, actor, clearance и методы login/logout
//   - НЕ содержит бизнес-логики
//   - НЕ принимает authorization-решений
//
// Ссылки:
//   - docs/API.md §4, §21.1
//   - docs/RBAC.md §4.1
//   - docs/CLASSIFICATION.md §5.1
//   - docs/SECURITY.md §5
//
// Изменения 1.1.2:
//   - useAuthBootstrap: добавлен bootstrappedRef, чтобы loadMe()
//     вызывался ровно один раз за жизненный цикл приложения.
//     Это гарантирует отсутствие бесконечного цикла, даже если
//     authStore.loadMe() при ошибке возвращает status в 'idle'.
//
// Изменения 1.1.1:
//   - isLoading теперь покрывает status === 'idle' (сессия ещё
//     не проверена) в дополнение к status === 'loading'.
//     Это устраняет мигание «Требуется вход» → «Revenue» при
//     первой отрисовке, когда useAuthBootstrap ещё не успел
//     вызвать loadMe().
// ============================================================

import { useEffect, useRef } from 'react';
import { useShallow } from 'zustand/react/shallow';
import {
  useAuthStore,
  selectActor,
  selectUser,
  selectClearance,
  selectAuthStatus,
  type AuthUser,
  type ClearanceSummary,
  type MfaChallenge,
  type MfaEnrolOptions,
} from '@/store/authStore';
import type { ActorRef } from '@/lib/entitySchema';

// ------------------------------------------------------------
// Публичный API
// ------------------------------------------------------------

export interface UseAuthResult {
  // Identity
  user: AuthUser | null;
  actor: ActorRef | null;
  clearance: ClearanceSummary | null;

  // Состояние
  status: ReturnType<typeof selectAuthStatus>;
  isAuthenticated: boolean;
  isLoading: boolean;

  // Действия
  login: (input: { email: string; password: string }) => Promise<AuthUser>;
  logout: () => Promise<void>;
  loadMe: () => Promise<void>;

  // MFA (ADR-0008)
  mfaChallenge: MfaChallenge | null;
  verifyMfa: (input: {
    method: 'totp' | 'recovery';
    code: string;
  }) => Promise<AuthUser>;
  enrolOptions: () => Promise<MfaEnrolOptions>;
  enrolMfa: (code: string) => Promise<{
    user: AuthUser;
    recoveryCodes: string[];
  }>;
  verifyWebauthn: (response: unknown) => Promise<AuthUser>;
  clearMfaChallenge: () => void;

  // Хелперы
  hasRole: (role: string) => boolean;
}

export function useAuth(): UseAuthResult {
  // Используем один shallow-селектор, чтобы не ре-рендерить
  // компонент при изменении несвязанных полей store.
  const {
    user,
    actor,
    clearance,
    status,
    isAuthenticatedRaw,
    login,
    logout,
    loadMe,
    hasRole,
    mfaChallenge,
    verifyMfa,
    enrolOptions,
    enrolMfa,
    verifyWebauthn,
    clearMfaChallenge,
  } = useAuthStore(
    useShallow((s) => ({
      user: selectUser(s),
      actor: selectActor(s),
      clearance: selectClearance(s),
      status: selectAuthStatus(s),
      isAuthenticatedRaw: s.isAuthenticated(),
      login: s.login,
      logout: s.logout,
      loadMe: s.loadMe,
      hasRole: s.hasRole,
      mfaChallenge: s.mfaChallenge,
      verifyMfa: s.verifyMfa,
      enrolOptions: s.enrolOptions,
      enrolMfa: s.enrolMfa,
      verifyWebauthn: s.verifyWebauthn,
      clearMfaChallenge: s.clearMfaChallenge,
    })),
  );

  const isAuthenticated = status === 'authenticated' && isAuthenticatedRaw;

  // 'idle' — сессия ещё не проверена (loadMe ещё не вызывался
  // или только что вызван, но ещё не завершился).
  // 'loading' — loadMe в процессе.
  // Оба состояния означают «мы ещё не знаем, кто пользователь»,
  // и для UI это одинаково — «загрузка».
  const isLoading = status === 'idle' || status === 'loading';

  return {
    user,
    actor,
    clearance,
    status,
    isAuthenticated,
    isLoading,
    login,
    logout,
    loadMe,
    hasRole,
    mfaChallenge,
    verifyMfa,
    enrolOptions,
    enrolMfa,
    verifyWebauthn,
    clearMfaChallenge,
  };
}

// ------------------------------------------------------------
// useAuthBootstrap
// ------------------------------------------------------------
//
// Один вызов на верхнем уровне приложения (например, в App.tsx
// или в Layout): при mount проверяем текущую сессию через
// GET /auth/me. Если cookie-flow с refresh — access token
// восстанавливается из refresh cookie.
//
// ВАЖНО:
//   - loadMe() вызывается ровно один раз за жизненный цикл
//     приложения (bootstrappedRef). Это защищает от бесконечного
//     цикла, если authStore.loadMe() при ошибке вернёт status
//     в 'idle' — useEffect больше не сработает повторно.
//   - не вызываем loadMe, если уже authenticated — избегаем
//     лишнего запроса.

export function useAuthBootstrap(): void {
  const status = useAuthStore(selectAuthStatus);
  const loadMe = useAuthStore((s) => s.loadMe);
  const bootstrappedRef = useRef(false);

  useEffect(() => {
    // Один вызов loadMe() за жизненный цикл приложения.
    // Даже если status вернётся в 'idle' из-за ошибки —
    // повторного вызова не будет.
    if (bootstrappedRef.current) return;

    if (status === 'idle') {
      bootstrappedRef.current = true;
      void loadMe();
    }
  }, [status, loadMe]);
}